import { CONFIG } from './config.ts';
import { ivShare, qualityShare, rollRatingExact, type Rolls } from './rolls.ts';
import { SPECIES } from './species.ts';

/**
 * How much each IV matters to a species, summing to 1: its base stat share,
 * leaving out the attack stat its style never uses (Focus for Might attackers,
 * Might for Focus attackers). Every stage of a line has the same shape, so
 * the weights never change when a creature ascends.
 */
const weightCache = new Map<string, number[]>();
export function statWeights(speciesId: string): number[] {
  let w = weightCache.get(speciesId);
  if (w) return w;
  // Use the final form, so rounding in each stage's base stats never shifts a rating.
  // An Awakened form rates on its base species, so awakening never moves a grade.
  let sp = SPECIES[SPECIES[speciesId].awakenedFrom ?? speciesId];
  while (sp.evolvesTo) sp = SPECIES[sp.evolvesTo];
  const unused = sp.style === 'might' ? 3 : 1;
  const raw = sp.base.map((b, i) => (i === unused ? 0 : b));
  const sum = raw.reduce((a, b) => a + b, 0);
  w = raw.map((b) => b / sum);
  weightCache.set(speciesId, w);
  return w;
}

/**
 * The overall 0..100 rating, relative to the species: the rolls, with talent
 * weighted for the species, plus a small bonus per ascension stage so
 * ascending always raises it. Species strength shows in stats and Power.
 */
export function ratingOf(r: Rolls, speciesId: string): number {
  return Math.round(ratingExactOf(r, speciesId));
}

export function ratingExactOf(r: Rolls, speciesId: string): number {
  return Math.min(100, rollRatingExact(r, statWeights(speciesId)) + stageBonus(speciesId));
}

export function stageBonus(speciesId: string): number {
  return CONFIG.ratingPerStage * (SPECIES[speciesId].stage - 1);
}

/** Ten bands of ten points. A rating right on a boundary belongs to the band above. */
export const RATING_TIERS = [
  'Feeble', 'Weak', 'Plain', 'Fair', 'Good', 'Strong', 'Superb', 'Heroic', 'Legendary', 'Godlike',
] as const;

export function ratingTier(rating: number): { index: number; name: string; next?: string; toNext: number } {
  const index = Math.min(9, Math.max(0, Math.floor(rating / 10)));
  return { index, name: RATING_TIERS[index], next: RATING_TIERS[index + 1], toNext: index < 9 ? (index + 1) * 10 - rating : 0 };
}

/** Points each part contributes to the rating. */
export function ratingParts(r: Rolls, speciesId: string) {
  const p = CONFIG.ratingPoints;
  return {
    talent: p.talent * ivShare(r, statWeights(speciesId)),
    quality: p.quality * qualityShare(r),
    bloodline: CONFIG.bloodlineRating[r.bloodline],
    aura: r.aura ? p.aura : 0,
    stage: stageBonus(speciesId),
  };
}
